import React, { useState, useLayoutEffect, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Heading } from '@web/atoms';
import {
  BackgroundColorOverlay,
  ContentWrapper,
  FullWidthDivider,
  GradientOverlay,
  ImagePanel,
  InkIndicator,
  PanelBody,
  PanelContent,
  PanelHeadline,
  PanelImage,
  PanelOverlay,
  StyledTabbedShowcase,
  StyledTabPanelsWrapper,
  SubHeadline,
  Tab,
  TabBar,
} from './styles.js';

export const TabbedShowcase = ({
  headline,
  headlineTypography = 'heading1',
  fontColor = 'text.primary',
  subheadline,
  backgroundColor = 'background.base',
  tabPanels,
}) => {
  const [activeTab, setActiveTab] = useState(tabPanels[0]?.tabLabel);
  const [visibleTab, setVisibleTab] = useState(tabPanels[0]?.tabLabel);
  const [imgFading, setImgFading] = useState(false);
  const btnRefs = useRef({});
  const railRef = useRef(null);
  const inkRef = useRef(null);
  const timerRef = useRef(null);

  // activeTab drives ink position and tab styling immediately.
  // visibleTab drives the image panel and is delayed to create a cross-fade:
  // the outgoing image fades out while the text still shows the old panel's
  // content, then both flip once the fade completes.
  const switchTab = tabLabel => {
    if (tabLabel === activeTab) return;
    // Scroll the tab into view horizontally when the bar overflows on mobile.
    btnRefs.current[tabLabel]?.scrollIntoView({
      inline: 'nearest',
      block: 'nearest',
    });
    setActiveTab(tabLabel);
    setImgFading(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setVisibleTab(tabLabel);
      setImgFading(false);
    }, 220);
  };

  useEffect(() => () => clearTimeout(timerRef.current), []);

  // Derived from visibleTab so the panel text holds until the cross-fade ends.
  const currentPanel = tabPanels?.find(
    ({ tabLabel }) => tabLabel === visibleTab,
  );

  // Measures the active tab button's position relative to the rail and writes
  // directly to the ink indicator's DOM style, avoiding a React re-render.
  const updateInk = () => {
    const btn = btnRefs.current[activeTab];
    const rail = railRef.current;
    const ink = inkRef.current;
    if (!btn || !rail || !ink) return;
    const railRect = rail.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    // getBoundingClientRect() is viewport-relative, but position:absolute inside
    // a scrollable container is content-relative, so scrollLeft must be added.
    ink.style.left = `${btnRect.left - railRect.left + rail.scrollLeft}px`;
    ink.style.width = `${btnRect.width}px`;
  };

  // Ref so async callbacks (ResizeObserver, fonts.ready, resize) always call
  // the latest closure without needing to re-register when activeTab changes.
  const updateInkRef = useRef(updateInk);
  updateInkRef.current = updateInk;

  useEffect(() => {
    const handler = () => updateInkRef.current();
    // ResizeObserver re-measures when a tab's font-weight toggle changes its
    // width, firing after layout is computed with the new styles applied.
    const observer = new ResizeObserver(handler);
    Object.values(btnRefs.current).forEach(btn => {
      if (btn) observer.observe(btn);
    });
    // Correct the initial measurement once the web font is ready, since
    // getBoundingClientRect on mount may use fallback font metrics.
    document.fonts.ready.then(handler);
    window.addEventListener('resize', handler);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handler);
    };
  }, []);

  // Positions the ink synchronously before paint on each tab change.
  useLayoutEffect(updateInk, [activeTab]);

  return (
    <StyledTabbedShowcase $backgroundColor={backgroundColor}>
      <ContentWrapper>
        <Heading
          headingLevel="h1"
          typography={headlineTypography}
          headingText={headline}
          horizontalPadding={2}
          verticalPadding={1}
          backgroundColor={backgroundColor}
          fontColor={fontColor}
          position="center"
        />
        {subheadline && <SubHeadline>{subheadline}</SubHeadline>}
        <StyledTabPanelsWrapper $oneTab={tabPanels?.length === 1}>
          {tabPanels?.length > 1 && (
            <>
              <TabBar
                ref={railRef}
                role="tablist"
                aria-label="Ollie product tabs"
                $alignTabs={tabPanels?.length > 3 ? 'flex-start' : 'center'}
              >
                {tabPanels?.map(
                  ({ tabLabel, activeLabelColor, inactiveLabelColor }) => (
                    <Tab
                      key={tabLabel}
                      ref={el => {
                        btnRefs.current[tabLabel] = el;
                      }}
                      id={`bb-tab-${tabLabel}`}
                      role="tab"
                      aria-selected={activeTab === tabLabel}
                      aria-controls="bb-panel"
                      $isActive={activeTab === tabLabel}
                      $activeLabelColor={activeLabelColor}
                      $inactiveLabelColor={inactiveLabelColor}
                      onClick={() => switchTab(tabLabel)}
                    >
                      {tabLabel}
                    </Tab>
                  ),
                )}
                <InkIndicator aria-hidden="true" ref={inkRef} />
              </TabBar>
              <FullWidthDivider />
            </>
          )}
          <ImagePanel
            role="tabpanel"
            id="bb-panel"
            aria-labelledby={
              tabPanels?.length > 1 ? `bb-tab-${activeTab}` : undefined
            }
            $oneTab={tabPanels?.length === 1}
          >
            {tabPanels?.map(
              ({ tabLabel, image, headline, mobileImageHorizontalPosition }) =>
                image ? (
                  <PanelImage
                    key={tabLabel}
                    src={image?.src ?? image}
                    alt={image?.alt ?? headline}
                    fill
                    objectFit="cover"
                    sizes="(max-width: 640px) 100vw, 1200px"
                    $visible={tabLabel === visibleTab && !imgFading}
                    $mobileImageHorizontalPosition={
                      mobileImageHorizontalPosition
                    }
                  />
                ) : null,
            )}
            {currentPanel?.backgroundColor && !currentPanel?.image ? (
              <BackgroundColorOverlay
                data-testid="background-color-overlay"
                $backgroundColor={currentPanel.backgroundColor}
              />
            ) : (
              <GradientOverlay aria-hidden="true" />
            )}
            {currentPanel?.overlay?.[0] && (
              <PanelOverlay $visible={!imgFading}>
                {currentPanel.overlay[0]}
              </PanelOverlay>
            )}
            {currentPanel && (
              <PanelContent $fading={imgFading}>
                <PanelHeadline
                  $typography={currentPanel.typography}
                  $fontColor={currentPanel.fontColor}
                >
                  {currentPanel.headline}
                </PanelHeadline>
                {currentPanel.subheadline && (
                  <PanelBody>{currentPanel.subheadline}</PanelBody>
                )}
              </PanelContent>
            )}
          </ImagePanel>
        </StyledTabPanelsWrapper>
      </ContentWrapper>
    </StyledTabbedShowcase>
  );
};

TabbedShowcase.displayName = 'TabbedShowcase';

TabbedShowcase.propTypes = {
  backgroundColor: PropTypes.string,
  fontColor: PropTypes.string,
  headline: PropTypes.string,
  headlineTypography: PropTypes.string,
  subheadline: PropTypes.node,
  tabPanels: PropTypes.arrayOf(
    PropTypes.shape({
      tabLabel: PropTypes.string,
      headline: PropTypes.string,
      typography: PropTypes.string,
      fontColor: PropTypes.string,
      activeLabelColor: PropTypes.string,
      inactiveLabelColor: PropTypes.string,
      subheadline: PropTypes.node,
      image: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      mobileImageHorizontalPosition: PropTypes.number,
      backgroundColor: PropTypes.string,
      overlay: PropTypes.arrayOf(PropTypes.node),
    }),
  ).isRequired,
};
